/**
 * 錯誤邊界組件
 * 捕獲並顯示 React 組件樹中的錯誤
 */
import { Component, ErrorInfo, ReactNode } from 'react';
import toast from 'react-hot-toast';
import { logErrorToServer } from '../utils/logger.js';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // 發送錯誤到伺服器
    logErrorToServer(error, {
      componentStack: errorInfo.componentStack || undefined,
      context: {
        componentName: errorInfo.componentStack?.split('\n')[0] || 'Unknown',
      },
    });

    // 顯示錯誤 toast
    toast.error(`應用程式錯誤: ${error.message}`);
  }

  public render() {
    if (this.state.hasError) {
      return (
        this.props.fallback || (
          <div className="min-h-screen bg-gray-50 flex items-center justify-center">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-8 text-center">
              <div className="mb-6">
                <svg
                  className="w-16 h-16 text-red-500 mx-auto mb-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                  發生錯誤
                </h1>
                <p className="text-gray-600 mb-4">
                  應用程式遇到未預期的錯誤，請重新載入頁面或聯繫技術支援。
                </p>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-red-700">
                  {this.state.error?.message || '未知錯誤'}
                </p>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => window.location.reload()}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
                >
                  重新載入
                </button>
                <button
                  onClick={() =>
                    this.setState({ hasError: false, error: undefined })
                  }
                  className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
                >
                  重試
                </button>
              </div>
            </div>
          </div>
        )
      );
    }

    return this.props.children;
  }
}
